import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NHCQRNFCPanel, NHCNeoVerifyBlock, T } from '@design/index';
import { govPageContainer } from '../ui/PatientUIGovernor';
import { loadPatientIdentity } from '../utils/patientIdentity';
import type { PatientIdentity } from '../utils/patientIdentity';
import Spinner from '../../../components/ui/Spinner';
import ApprovedPatientCard from '../components/ApprovedPatientCard';

function IdRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
      <span style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 10, fontWeight: 700, color: T.text, fontFamily: mono ? T.mono : T.font }}>{value}</span>
    </div>
  );
}

function ActionRow({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 14px', background: T.white, border: `1px solid ${T.border}`,
      borderRadius: 12, cursor: 'pointer', fontFamily: T.font,
    }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: `${T.teal}10`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.text, textAlign: 'left' }}>{label}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round"><path d="m9 18 6-6-6-6" /></svg>
    </button>
  );
}

export default function PatientNHCCard() {
  const navigate = useNavigate();
  const [id, setId] = useState<PatientIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadPatientIdentity()
      .then(({ identity }) => setId(identity))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !id) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  const handleCopy = () => {
    navigator.clipboard?.writeText(id.nhcIdRaw).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ ...govPageContainer({ padding: '14px 16px 100px' }), display: 'flex', flexDirection: 'column', gap: 14 }}>

      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <ApprovedPatientCard
          name={id.patientName}
          nhcId={id.nhcIdDisplay}
          nhcIdRaw={id.nhcIdRaw}
          bloodGroup={id.bloodGroup}
          insuranceStatus={id.insuranceStatus}
        />
      </div>

      <NHCQRNFCPanel
        nhcId={id.nhcIdDisplay}
        label="Present this QR at any NeoHealthCard-enabled facility"
      />

      <div style={{
        background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
        padding: 14, boxShadow: T.shadow,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font }}>NeoHealthCard ID Details</span>
          <button onClick={handleCopy} style={{
            fontSize: 9, fontWeight: 600, color: copied ? T.success : T.teal,
            background: copied ? `${T.success}10` : `${T.teal}10`,
            border: `1px solid ${copied ? T.success : T.teal}25`, borderRadius: 8,
            padding: '4px 10px', cursor: 'pointer', fontFamily: T.font,
          }}>
            {copied ? 'Copied!' : 'Copy ID'}
          </button>
        </div>
        <IdRow label="NeoHealthCard ID" value={id.nhcIdDisplay} mono />
        <IdRow label="Patient Name" value={id.patientName} />
        <IdRow label="Blood Group" value={id.bloodGroup} />
        <IdRow label="Date of Birth" value={id.dob} />
        <IdRow label="Gender" value={id.gender} />
        <IdRow label="Phone" value={id.phone} />
        <IdRow label="Insurance Status" value={id.insuranceStatus} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
          <div style={{ width: 6, height: 6, borderRadius: 3, background: id.verifiedStatus ? T.success : T.warning }} />
          <span style={{ fontSize: 9, fontWeight: 600, color: id.verifiedStatus ? T.success : T.warning, fontFamily: T.font }}>
            {id.verifiedStatus ? 'Verified Patient' : 'Verification Pending'}
          </span>
        </div>
      </div>

      <NHCNeoVerifyBlock
        nhcId={id.nhcIdDisplay}
        entityName={id.patientName}
        entityType="Verified Patient"
        verified={id.verifiedStatus}
      />

      <div style={{
        background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
        padding: 14, boxShadow: T.shadow,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 10 }}>ABHA / ABDM Linking</div>
        <IdRow label="ABHA ID" value={id.abhaId || 'Not linked'} mono={!!id.abhaId} />
        <div style={{
          marginTop: 8, fontSize: 9, color: T.textSec, fontFamily: T.font, lineHeight: 1.5,
        }}>
          Link your ABHA (Ayushman Bharat Health Account) to enable government health scheme integration and unified health records under ABDM.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button
          onClick={() => navigate('/patient/consent')}
          style={{
            width: '100%', padding: '13px 0', borderRadius: 12, border: 'none',
            background: T.teal, color: '#FFF', fontSize: 13, fontWeight: 700,
            fontFamily: T.font, cursor: 'pointer',
          }}
        >
          Share with Provider
        </button>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <ActionRow
            label="Emergency Card"
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round"><path d="M12 9v4 M12 17h.01 M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>}
            onClick={() => navigate('/patient/emergency')}
          />
          <ActionRow
            label="Access Log"
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round"><path d="M12 7v5l3 2 M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z" /></svg>}
            onClick={() => navigate('/patient/access-logs')}
          />
        </div>
      </div>

      <div style={{
        background: `${T.teal}08`, border: `1px solid ${T.teal}18`, borderRadius: 12,
        padding: 12, fontSize: 9, color: T.textSec, fontFamily: T.font, lineHeight: 1.6, textAlign: 'center',
      }}>
        QR/NFC verifies identity first. Medical record access requires consent or emergency authorization. All access is audit logged.
      </div>
    </div>
  );
}
